import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Switch from '@mui/material/Switch';
import "../../../styles/Auth/attribute.css";
import FullscreenLoader from "../../../components/Common/FullscreenLoader.jsx";
import { Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Paper, Typography, Dialog, DialogTitle,
    DialogContent, DialogActions, Box, Modal, Button
} from '@mui/material';
import { Icon } from "@iconify/react";
import plusIcon from '@iconify/icons-mdi/eye-circle-outline';
import editIcon from '@iconify/icons-mdi/square-edit-outline';
import deleteIcon from '@iconify/icons-mdi/trash-can-outline';
import { postData, uploadFile, getData, putData, deleteData } from "../../../webService/webservice";
import closeIcon from '@iconify/icons-mdi/close-circle-outline';
import { toast } from "react-toastify";


const AttributesPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const isAddPage = location.pathname === '/attributes/add';
    const commonCellStyle = {
        color: '#525252',
        fontSize: '12px',
        fontWeight: 600,
        display: "flex",
        alignItems: 'center',
        justifyContent: "left"
    };
    const headings = [
        'SL',
        'Name',
        'Values',
        'Status',
        'Action',
    ];
    const [refreshKey, setRefreshKey] = useState(0);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [Attributes, setAttributes] = useState([]);
    const [limit, setLimit] = useState(5);
    const [searchQuery, setSearchQuery] = useState('');
    const sessionToken = sessionStorage.getItem("sessionToken");
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [open, setOpen] = useState(false);
    const [isPopupMode, setIsPopupMode] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [editAttributeId, setEditAttributeId] = useState(null);
    const [AttributeToDelete, setAttributeToDelete] = useState(null);
    const [formData, setFormData] = useState({
        attributeName: "",
        values: []
    });
    const [openValuePopup, setOpenValuePopup] = useState(false);
    const [selectedAttribute, setSelectedAttribute] = useState(null);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [attributeText, setAttributeText] = useState("");

    const handleAddAttriute = async (e) => {
        e.preventDefault();
        let newErrors = {};

        // Attribute Name validation
        if (!formData.attributeName?.trim()) {
            newErrors.attributeName = "Attribute Name is required.";
        }

        // Attribute Values validation
        if (!formData.values || formData.values.length === 0) {
            newErrors.values = "At least one attribute value is required.";
        } else {
            const nonEmptyValues = formData.values.filter(v => v.value.trim() !== "");
            if (nonEmptyValues.length === 0) {
                newErrors.values = "Attribute values cannot be empty.";
            }
        }

        setErrors(newErrors);
        if (Object.keys(newErrors).length > 0) return;

        // ⭐ Clean & format values
        const seen = new Set();
        let cleanedValues = [];

        formData.values.forEach(v => {
            let val = v.value.trim();
            if (!val) return;

            // Auto-capitalize the first letter
            val = val.charAt(0).toUpperCase() + val.slice(1);

            const lower = val.toLowerCase();

            // Prevent case-insensitive duplicates
            if (!seen.has(lower)) {
                seen.add(lower);
                cleanedValues.push(val);
            }
        });

        // Sort alphabetically
        cleanedValues = cleanedValues.sort((a, b) =>
            a.localeCompare(b, undefined, { sensitivity: "base" })
        );

        // Convert to API format
        const formattedValues = cleanedValues.map(v => ({
            value: v,
            isActive: true
        }));

        // Replace formData values
        formData.values = formattedValues;

        toast.info("Values cleaned & duplicates removed.");

        setLoading(true);

        try {
            const token = sessionStorage.getItem("sessionToken");

            const AttributesData = {
                attributeName: formData.attributeName.trim(),
                values: formattedValues
            };

            console.log("Sending Attributes data:", AttributesData);

            const response = await postData("attributes/", AttributesData, token);
            toast.success(response.message || "Attributes added successfully");

            // Reset form
            setFormData({ attributeName: "", values: [] });
            navigate("/attributes");
            triggerRefresh();

        } catch (error) {
            console.error("Add Attribute Error:", error);
            toast.error(error.message || "Add Attribute Failed");
        } finally {
            setLoading(false);
        }
    };


    useEffect(() => {
        const fetchAttributes = async () => {
            try {
                const params = {
                    page: currentPage,
                    limit,
                    attributeName: searchQuery?.trim() || "",
                };
                const data = await getData('attributes/attributes', params, sessionToken);
                const results = data?.data?.results?.results || [];
                setAttributes(results);
                setTotalPages(data?.data?.results?.totalPages || 1);
                setTotalCount(data?.data?.results?.totalItems || 0);
            } catch (error) {
                toast.error(error.message || "Failed to fetch attributes");
            }
        };
        fetchAttributes();
    }, [currentPage, limit, refreshKey, searchQuery]);

    const handleUpdateAttribute = async (e) => {
        e.preventDefault();

        let newErrors = {};

        // Validation
        if (!formData.attributeName?.trim()) {
            newErrors.attributeName = "Attribute Name is required.";
        }

        if (!formData.values || formData.values.length === 0) {
            newErrors.values = "At least one attribute value is required.";
        }

        setErrors(newErrors);
        if (Object.keys(newErrors).length > 0) return;

        // ⭐ CLEANING STEPS ⭐
        const seen = new Set();
        let cleanedValues = [];

        formData.values.forEach(v => {
            let val = v.value.trim();

            if (!val) return;

            // ✔ Auto-capitalize first letter, keep original case
            val = val.charAt(0).toUpperCase() + val.slice(1);

            const lower = val.toLowerCase();

            // ✔ Case-insensitive duplicate removal
            if (!seen.has(lower)) {
                seen.add(lower);
                cleanedValues.push(val);
            }
        });

        // ✔ Sort alphabetically after cleaning
        cleanedValues = cleanedValues.sort((a, b) =>
            a.localeCompare(b, undefined, { sensitivity: 'base' })
        );

        // Convert sorted strings into required format
        const formattedValues = cleanedValues.map(v => ({ value: v }));

        // Replace form values fully
        formData.values = formattedValues;

        setLoading(true);

        try {
            const token = sessionStorage.getItem("sessionToken");

            // ⭐ Overwrite old DB values completely
            const updatedAttributeData = {
                attributeName: formData.attributeName.trim(),
                values: formattedValues
            };

            console.log("Final Update Payload:", updatedAttributeData);

            const response = await putData(
                `attributes/attribute/${editAttributeId}`,
                updatedAttributeData,
                token
            );

            toast.success(response.message || "Attribute updated successfully");

            // Reset UI
            setFormData({ attributeName: "", values: [] });
            setIsPopupMode(false);
            setIsEditMode(false);
            triggerRefresh();

        } catch (error) {
            console.error("Error updating attribute:", error);
            toast.error("Error updating attribute.");
        } finally {
            setLoading(false);
        }
    };


    const handleDeleteAttribute = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const token = sessionStorage.getItem('sessionToken');
            const response = await deleteData(`attributes/attribute/${AttributeToDelete}`, token);
            toast.success(response.message || "Attribute deleted successfully");
            setAttributeToDelete(null);
            handleClose();
            triggerRefresh();
        } catch (error) {
            console.error("Delete error:", error);
            toast.error(error.message || "Failed to delete Attribute");
        } finally {
            setLoading(false);
        }
    };

    const handleToggle = async (attributeId, newStatus) => {
        try {
            const token = sessionStorage.getItem("sessionToken");

            const response = await putData(
                `attributes/toggle-status/${attributeId}`,
                { isActive: newStatus },    // send true or false
                token
            );

            toast.success(response.message || "Status updated!");

            triggerRefresh();
        } catch (error) {
            console.error("Toggle Error:", error);
            toast.error("Failed to update status");
        }
    };

    const handleOpenValues = (attribute) => {
        setSelectedAttribute(attribute);
        setOpenValuePopup(true);
    };

    const handleClosePopup = () => {
        setOpenValuePopup(false);
        setSelectedAttribute(null);
    };

    const triggerRefresh = () => {
        setRefreshKey(prev => prev + 1);
    };

    const modalStyle = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 400,
        bgcolor: '#e6cf80',
        borderRadius: 8,
        boxShadow: 50,
        p: 4,
        border: 0
    };

    const handleOpen = (attributeId) => {
        setAttributeToDelete(attributeId);
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setAttributeToDelete(null);
    };

    const handlePageChange = (direction) => {
        setCurrentPage((prev) =>
            direction === "next" ? Math.min(prev + 1, totalPages) : Math.max(prev - 1, 1)
        );
    };

    const handleItemsPerPageChange = (e) => {
        const value = e.target.value;
        const newLimit = value === "all" ? "all" : Number(value);
        setLimit(newLimit);
        setCurrentPage(1);
    };

    useEffect(() => {
        if (isEditMode && formData.values.length) {
            setAttributeText(
                formData.values.map(v => v.value).join(", ")
            );
        }
    }, [isEditMode]);

    return (
        <div className="Attribute-layout">
            {loading && <FullscreenLoader />}
            <div className="Attribute-content">
                <main className="Attribute-main">
                    {isAddPage ? (
                        <div className="add-attribute-form">
                            <h3>Add Attribute</h3>
                            <form className="attribute-form">
                                <div className="attribute-form-row">

                                    {/* Attribute Name */}
                                    <div className="attribute-form-group full-width">
                                        <label htmlFor="attributeId" className="input-label">
                                            Attribute Name<span className="required">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="Enter Attribute name"
                                            className={`attribute-input ${errors.attributeName ? "input-error" : ""}`}
                                            onChange={e =>
                                                setFormData(prev => ({ ...prev, attributeName: e.target.value }))
                                            }
                                        />
                                        {errors.attributeName && (
                                            <p className="error-msg">{errors.attributeName}</p>
                                        )}
                                    </div>

                                    {/* Attribute Values - Bigger Text Area */}
                                    <div className="attribute-form-group full-width">
                                        <label htmlFor="values" className="input-label">
                                            Attribute Values<span className="required">*</span>
                                        </label>
                                        <textarea
                                            placeholder="Enter values separated by comma (e.g. Red, Blue, Green)"
                                            rows={4}
                                            className={`attribute-textarea ${errors.values ? "input-error" : ""}`}
                                            onChange={e =>
                                                setFormData(prev => ({
                                                    ...prev,
                                                    values: e.target.value
                                                        .split(',')
                                                        .map(v => ({ value: v.trim(), isActive: true }))
                                                }))
                                            }
                                        />
                                        <p className="hint-text">Example: Silk, Cotton, Organza, Net</p>
                                        {errors.values && (
                                            <p className="error-msg">{errors.values}</p>
                                        )}
                                    </div>
                                </div>

                                <div className="attribute-buttons">
                                    <button className="add-attribute-button" onClick={handleAddAttriute}>
                                        + Add Attribute
                                    </button>
                                    <button className="cancel-attribute-button" onClick={() => navigate('/attributes')}>
                                        Cancel
                                    </button>
                                </div>
                            </form>

                        </div>
                    ) : (
                        <>

                            <div className="Attribute-widgets">
                                <div className="Attribute-widgets-header">
                                    <h3>Attributes</h3>
                                    
                                </div>
                                <button className="add-btn-attribute"
                                    onClick={() => navigate('/attributes/add')}
                                >+ Add Attributes</button>
                            </div>
                            <TableContainer component={Paper} sx={{
                                borderRadius: '12px',
                                overflow: 'outo',
                                maxHeight: '400px',
                                overflowY: "auto",
                                '&::-webkit-scrollbar': { display: 'none' },
                                scrollbarWidth: 'none',
                                msOverflowStyle: 'none'
                            }}>
                                <Table className="custom-SubCategory-table"
                                    sx={{
                                        '& td, & th': {
                                            border: 'none',
                                        },
                                        border: 'none',
                                    }}>
                                    <TableHead className="custom-SubCategory-head">
                                        <TableRow >
                                            {headings.map((heading, index) => (
                                                <TableCell
                                                    key={index}
                                                    sx={{
                                                        border: 'none',
                                                        padding: '12px 16px',
                                                        textAlign: 'center',
                                                        position: "sticky",
                                                        top: 0,
                                                        backgroundColor: '#e6cf80',
                                                        zIndex: 1,
                                                    }}
                                                >
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            display: 'flex',
                                                            gap: '4px',
                                                            color: '#000000',
                                                            fontSize: '14px',
                                                            fontWeight: 600,
                                                            alignItems: 'center',
                                                            justifyContent: "left"
                                                        }}
                                                    >
                                                        {heading}
                                                    </Typography>
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {Attributes.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={5}>
                                                    <Typography sx={{ textAlign: 'center', padding: '20px', fontWeight: 500 }}>
                                                        No Attributes found.
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            Attributes.map((att, index) => (
                                                <TableRow key={att._id}>
                                                    <TableCell>
                                                        <Typography sx={commonCellStyle}>{index + 1}</Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography sx={commonCellStyle}>{att.attributeName}</Typography>
                                                    </TableCell>
                                                    {/* <TableCell>
                                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
                                                            {att.values.map(val => (
                                                                <Typography
                                                                    key={val._id}
                                                                    sx={{
                                                                        fontSize: '12px',
                                                                        padding: '2px 6px',
                                                                        borderRadius: '6px',
                                                                        backgroundColor: '#f0f0f0',
                                                                        fontWeight: 500,
                                                                    }}
                                                                >
                                                                    {val.value}
                                                                </Typography>
                                                            ))}
                                                        </Box>
                                                    </TableCell> */}
                                                    <TableCell>
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                justifyContent: 'left',
                                                                cursor: 'pointer',
                                                                color: '#1976d2',
                                                                fontWeight: 600
                                                            }}
                                                            onClick={() => handleOpenValues(att)}   // Open popup
                                                        >
                                                            {att.values.length} Values
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell >
                                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'left' }}>
                                                            <Switch
                                                                checked={att.isActive}
                                                                onChange={(e) => handleToggle(att._id, e.target.checked)}   // send id + status
                                                                sx={{
                                                                    '& .MuiSwitch-switchBase.Mui-checked': {
                                                                        color: '#28A745',
                                                                    },
                                                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                                        backgroundColor: '#28A745',
                                                                    },
                                                                    '& .MuiSwitch-track': {
                                                                        backgroundColor: '#C10000',
                                                                    },
                                                                }}
                                                            />
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', justifyContent: 'left' }}>
                                                            <Icon
                                                                icon={editIcon}
                                                                width={20}
                                                                height={20}
                                                                style={{ cursor: "pointer" }}
                                                                onClick={() => {

                                                                    setFormData({
                                                                        attributeName: att.attributeName || '',
                                                                        values: att.values.map(v => ({
                                                                            _id: v._id,
                                                                            value: v.value,
                                                                            isDelete: false
                                                                        }))
                                                                    });

                                                                    setEditAttributeId(att._id);
                                                                    setIsPopupMode(true);
                                                                    setIsEditMode(true);
                                                                }}
                                                            />
                                                            <Icon icon={deleteIcon} width={20} height={20} style={{ color: '#C10000', cursor: 'pointer' }}
                                                                onClick={() => handleOpen(att._id)}
                                                            />
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <Dialog open={openValuePopup} onClose={handleClosePopup} fullWidth maxWidth="sm">
                                <DialogTitle>
                                    {selectedAttribute?.attributeName} – Values
                                </DialogTitle>

                                <DialogContent dividers>
                                    {selectedAttribute?.values?.map(val => (
                                        <Typography
                                            key={val._id}
                                            sx={{
                                                padding: "6px 10px",
                                                marginBottom: "8px",
                                                background: "#f8f8f8",
                                                borderRadius: "6px",
                                                fontSize: "14px",
                                                fontWeight: 500
                                            }}
                                        >
                                            {val.value}
                                        </Typography>
                                    ))}

                                    {selectedAttribute?.values?.length === 0 && (
                                        <Typography>No values available</Typography>
                                    )}
                                </DialogContent>

                                <DialogActions>
                                    <Button onClick={handleClosePopup} variant="contained" color="primary">
                                        Close
                                    </Button>
                                </DialogActions>
                            </Dialog>
                            <div className="product-bottom">
                                <div className="product-section show-entries">
                                    <span className="text-label">Show</span>
                                    <select className="entries-select" value={limit}
                                        onChange={handleItemsPerPageChange}
                                    >
                                        <option value="5">5</option>
                                        <option value="10">10</option>
                                        <option value="25">25</option>
                                        <option value="all">All</option>
                                    </select>
                                    <span className="text-label">Entries</span>
                                </div>
                                <div className="product-section showing-range">
                                    {
                                        limit === 'all'
                                            ? `Showing all ${totalCount} entries`
                                            : `Showing ${(currentPage - 1) * limit + 1} to ${Math.min(currentPage * limit, totalCount)} of ${totalCount} entries`
                                    }
                                </div>
                                <div className="product-section spacer"></div>
                                <div className="product-section pagination-buttons">
                                    <button
                                        className="pagination-btn"
                                        disabled={currentPage === 1}
                                        onClick={() => handlePageChange("prev")}
                                    >Previous</button>
                                    <button
                                        className="pagination-btn"
                                        disabled={currentPage === totalPages}
                                        onClick={() => handlePageChange("next")}
                                    >Next</button>
                                </div>
                            </div>
                            {isPopupMode && (
                            <div className="popup-overlay">
                                <div className="popup-wrapper">
                                    <div className="popup-form">
                                        <h2 className="popup-title">
                                            Edit Attribute
                                        </h2>

                                        <form className="popup-form-body" onSubmit={handleUpdateAttribute}>

                                            {/* ATTRIBUTE NAME */}
                                            <div className="attribute-form-group">
                                                <label className="input-label">
                                                    Attribute Name <span className="required">*</span>
                                                </label>

                                                <input
                                                    type="text"
                                                    className={`attribute-input ${errors.attributeName ? "input-error" : ""}`}
                                                    placeholder="Enter attribute name"
                                                    value={formData.attributeName}
                                                    onChange={(e) =>
                                                        setFormData(prev => ({
                                                            ...prev,
                                                            attributeName: e.target.value
                                                        }))
                                                    }
                                                />

                                                {errors.attributeName && (
                                                    <p className="error-msg">{errors.attributeName}</p>
                                                )}
                                            </div>

                                            {/* ATTRIBUTE VALUES */}
                                            <div className="attribute-form-group">
                                                <label className="input-label">
                                                    Attribute Values <span className="required">*</span>
                                                </label>

                                                <textarea
                                                    className={`attribute-textarea ${errors.values ? "input-error" : ""}`}
                                                    rows={4}
                                                    placeholder="Enter values separated by comma"
                                                    value={attributeText}
                                                    onChange={(e) => {
                                                        let text = e.target.value;

                                                        // Auto-add comma before new entry if missing
                                                        if (
                                                            attributeText.length > 0 &&              // Existing text present
                                                            text.length > attributeText.length &&    // User is typing forward
                                                            !text.trim().endsWith(",") &&            // User didn't type comma manually
                                                            !text.includes(", ") &&                  // No space after comma
                                                            !text.endsWith(", ")                     // Already ends with comma space
                                                        ) {
                                                            // Automatically format with comma + space
                                                            text = text.replace(/,\s*$/, "");
                                                        }

                                                        setAttributeText(text);

                                                        // Convert text to array for backend
                                                        const arr = text
                                                            .split(",")
                                                            .map(v => v.trim())
                                                            .filter(v => v !== "")
                                                            .map(v => ({ value: v }));

                                                        setFormData(prev => ({
                                                            ...prev,
                                                            values: arr
                                                        }));
                                                    }}
                                                ></textarea>
                                                <p className="hint-text">Example: Silk, Cotton, Organza, Net</p>
                                                {errors.values && (
                                                    <p className="error-msg">{errors.values}</p>
                                                )}
                                            </div>

                                            {/* ACTION BUTTONS */}
                                            <div className="attribute-buttons">
                                                <button type="submit" className="add-attribute-button">
                                                    Update Attribute
                                                </button>

                                                <button
                                                    type="button"
                                                    className="cancel-attribute-button"
                                                    onClick={() => {
                                                        setIsPopupMode(false);
                                                        setIsEditMode(false);
                                                    }}
                                                >
                                                    Cancel
                                                </button>
                                            </div>

                                        </form>
                                    </div>
                                </div>
                            </div>
                            )}


                            {/* {limit > 5 && Attributes.length > 5 && (
                                <div className="scroll-down-hint">
                                    <Typography sx={{ display: 'flex', justifyContent: 'center', marginTop: 2, fontSize: 14, fontWeight: 600 }}>
                                        Scroll down to see more Attributes
                                    </Typography>
                                </div>
                            )} */}
                            <Modal
                                open={open}
                                onClose={handleClose}
                                aria-labelledby="modal-modal-title"
                                aria-describedby="modal-modal-description"
                            >
                                <Box sx={modalStyle}>
                                    <Typography id="modal-modal-title" variant="h6" component="h2" sx={{ textAlign: 'center' }}>
                                        Confirm Delete?
                                    </Typography>
                                    <Typography id="modal-modal-description" sx={{ mt: 1, fontSize: 14, textAlign: 'center' }}>
                                        Are you sure you want to delete this Attribute?
                                    </Typography>

                                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 3 }}>
                                        <Button variant="contained" color="error" sx={{ background: "#8D2629 " }} onClick={handleDeleteAttribute}>
                                            Yes, Delete
                                        </Button>
                                        <Button variant="outlined" color="error" onClick={handleClose} sx={{ background: 'background-color: #D9D9D9', color: '#000' }}>
                                            Cancel
                                        </Button>
                                    </Box>
                                </Box>
                            </Modal>
                        </>
                    )}

                </main>
            </div>
        </div>
    );
};

export default AttributesPage;